import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Role } from "@xinghai/shared";
import { ApiError, authApi } from "../../lib/api";
import type { MeResponse } from "../../lib/api";
import { qk } from "../../lib/queries";

/** A 401 is not an error here — it just means "not signed in". */
async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await authApi.me();
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthorized) return null;
    throw err;
  }
}

export interface AuthState {
  me: MeResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  roles: Role[];
  /** 红线 14.6 — student pages redirect to /consent while true. */
  consentRequired: boolean;
  isMinor: boolean;
  refetch: () => void;
}

export function useAuth(): AuthState {
  const q = useQuery({
    queryKey: qk.me,
    queryFn: fetchMe,
    retry: false,
    staleTime: 30_000,
  });
  return {
    me: q.data ?? null,
    isLoading: q.isPending,
    isError: q.isError,
    error: q.error,
    roles: q.data?.user?.roles ?? [],
    consentRequired: q.data?.consent_required ?? false,
    isMinor: q.data?.is_minor ?? false,
    refetch: () => void q.refetch(),
  };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      username,
      password,
      remember,
    }: {
      username: string;
      password: string;
      remember?: boolean;
    }) => authApi.login(username, password, remember ?? false),
    onSuccess: (data) => {
      qc.setQueryData(qk.me, data);
      void qc.invalidateQueries({ queryKey: qk.assignments });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      qc.setQueryData(qk.me, null);
      qc.clear();
    },
  });
}

export function useSignConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      train: boolean;
      display: boolean;
      commercial: boolean;
      minor_guardian_signed?: boolean;
    }) => authApi.consent({ ...payload, signed_at: null }),
    onSuccess: (data) => {
      qc.setQueryData(qk.me, data);
      void qc.invalidateQueries({ queryKey: qk.assignments });
    },
  });
}

// Owner decision: there is no self-service withdrawal. A student who wants to
// withdraw contacts 叽喳科技 in writing; the course team handles it server-side.
